import { parseFlexibleTimestamp } from '../../fileParser';
import { normalizeUnit } from '../columnAssigner';
import type { ColumnAssignment, ParsedDataPoint } from '../types';

function medianMinutes(gaps: number[]): number {
  const sorted = [...gaps].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

/** Median of the first 20 positive gaps, in minutes. Null when none exist.
 *  Rows must already be sorted oldest-first. */
function detectIntervalMinutes(points: Array<{ ts: Date }>): number | null {
  if (points.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < Math.min(points.length, 21); i++) {
    const diff = (points[i].ts.getTime() - points[i - 1].ts.getTime()) / 60000;
    if (diff > 0) gaps.push(diff);
  }
  return gaps.length > 0 ? medianMinutes(gaps) : null;
}

export function extractSingleMeter(
  rows: unknown[][],
  assignment: ColumnAssignment,
  dataStartRow: number
): { dataPoints: ParsedDataPoint[]; warnings: string[]; metadata: Record<string, unknown> } {
  const { timestampIndex, demandIndex, demandUnit, unitColumnIndex } = assignment;
  const perRowUnits = assignment.mixedRowUnits === true && unitColumnIndex !== null;
  const rawPoints: Array<{ ts: Date; value: number; unit: 'kW' | 'kWh' | 'kVA' }> = [];
  let skipped = 0;
  // Rows whose own unit cell was blank or unreadable, counted so the count can be
  // disclosed rather than inferred from the absence of a complaint.
  let unlabeledRows = 0;

  for (let i = dataStartRow; i < rows.length; i++) {
    const row = rows[i] as unknown[];

    // Parse timestamp
    const rawTs = row[timestampIndex];
    let ts: Date | null = null;
    if (rawTs instanceof Date && !isNaN(rawTs.getTime())) {
      ts = rawTs;
    } else if (rawTs !== null && rawTs !== undefined) {
      ts = parseFlexibleTimestamp(String(rawTs));
    }
    if (!ts) { skipped++; continue; }

    // Parse demand — skip Date objects, handle BOM and commas
    const rawDemand = row[demandIndex];
    if (rawDemand instanceof Date) { skipped++; continue; }
    const demandStr = String(rawDemand ?? '').replace(/^﻿/, '').replace(/,/g, '');
    const demandVal = parseFloat(demandStr);
    if (!Number.isFinite(demandVal)) { skipped++; continue; }

    // Under mixed row units the row's own cell decides; `demandUnit` is only the
    // fallback for a row that fails to state one.
    let unit = demandUnit;
    if (perRowUnits) {
      const stated = normalizeUnit(row[unitColumnIndex as number]);
      if (stated) {
        unit = stated;
      } else {
        unlabeledRows++;
      }
    }

    rawPoints.push({ ts, value: demandVal, unit });
  }

  const metadata: Record<string, unknown> = {};
  const dataPoints: ParsedDataPoint[] = [];

  // Sort before measuring gaps, not after. National Grid exports newest-first,
  // so every gap in file order was negative, none were collected, the interval
  // came out null and the kWh -> kW conversion below was skipped — while the
  // assignment warning still told the user the conversion had been applied.
  // On 15-minute data that reported peak demand at a quarter of its real value.
  rawPoints.sort((a, b) => a.ts.getTime() - b.ts.getTime());

  if (perRowUnits) {
    // Every kWh row needs the interval, so the interval is computed once for the
    // whole file rather than per unit. A file that cannot supply one cannot be
    // converted, and reporting the kWh values as kW would understate the peak in
    // the direction that wrongly frees capacity — so it is refused upstream.
    const intervalMinutes = detectIntervalMinutes(rawPoints);
    const kwhRows = rawPoints.filter(p => p.unit === 'kWh').length;
    if (kwhRows > 0 && !(intervalMinutes && intervalMinutes > 0)) {
      metadata.mixedRowUnitsUnconvertible = true;
      return { dataPoints: [], warnings: [...assignment.warnings], metadata };
    }
    const unitCounts: Record<string, number> = {};
    for (const p of rawPoints) {
      unitCounts[p.unit] = (unitCounts[p.unit] ?? 0) + 1;
      const kw = p.unit === 'kWh' ? p.value * (60 / (intervalMinutes as number)) : p.value;
      dataPoints.push({ timestamp: p.ts.toISOString(), kw });
    }
    metadata.mixedRowUnits = true;
    metadata.rowUnitCounts = unitCounts;
    metadata.unlabeledUnitRows = unlabeledRows;
    if (kwhRows > 0) {
      metadata.kwhToKwConverted = true;
      metadata.detectedIntervalMinutes = intervalMinutes;
    }
    if (unitCounts.kVA) metadata.unitDisclosure = 'kVA';
  } else if (demandUnit === 'kWh' && rawPoints.length >= 2) {
    const intervalMinutes = detectIntervalMinutes(rawPoints);
    if (intervalMinutes && intervalMinutes > 0) {
      for (const p of rawPoints) {
        dataPoints.push({ timestamp: p.ts.toISOString(), kw: p.value * (60 / intervalMinutes) });
      }
      metadata.kwhToKwConverted = true;
      metadata.detectedIntervalMinutes = intervalMinutes;
    } else {
      for (const p of rawPoints) {
        dataPoints.push({ timestamp: p.ts.toISOString(), kw: p.value });
      }
    }
  } else if (demandUnit === 'kVA') {
    for (const p of rawPoints) {
      dataPoints.push({ timestamp: p.ts.toISOString(), kw: p.value });
    }
    metadata.unitDisclosure = 'kVA';
  } else {
    for (const p of rawPoints) {
      dataPoints.push({ timestamp: p.ts.toISOString(), kw: p.value });
    }
  }

  // Always sort ascending (handles scrambled-row files)
  dataPoints.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const warnings: string[] = [...assignment.warnings];
  if (skipped > 0) {
    warnings.push(`Skipped ${skipped} rows with unparseable timestamps or values`);
  }

  // Completeness check — warn when data covers fewer than 30 days
  if (dataPoints.length >= 2) {
    const first = new Date(dataPoints[0].timestamp).getTime();
    const last = new Date(dataPoints[dataPoints.length - 1].timestamp).getTime();
    const totalDays = (last - first) / (1000 * 60 * 60 * 24);
    if (totalDays < 30) {
      warnings.push(
        `Data covers only ${totalDays.toFixed(1)} days. NEC 220.87 is based on 1 year of maximum demand data; its Exception permits no less than a 30-day continuous recording.`
      );
    }
  }

  return { dataPoints, warnings, metadata };
}
